import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/i18n';
import { useTranslations } from 'next-intl';

export default function OracleWisdomsDropDown({
  navLinkClasses,
  isMobileNavOpen,
  toggleMobileNav,
  label,
  locale = DEFAULT_LOCALE
}) {
  const t = useTranslations('common.header.nav');
  const normalizedLocale = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownId = 'oracle-wisdom-menu';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleToggle = () => setIsOpen((open) => !open);
  const handleItemSelect = () => {
    setIsOpen(false);
    if (isMobileNavOpen && toggleMobileNav) toggleMobileNav();
  };

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        ref={buttonRef}
        onClick={handleToggle}
        className={`${navLinkClasses} inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`}
        aria-expanded={isOpen}
        aria-controls={dropdownId}
        aria-haspopup="menu"
      >
        <span className="mr-1">{label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          id={dropdownId}
          role="menu"
          aria-label={label}
          className="absolute top-full left-1/2 z-50 mt-3 w-48 -translate-x-1/2 rounded-xl border border-[#6A3683]/40 bg-[#050316]/95 py-2 shadow-[0_10px_30px_rgba(7,2,25,0.45)]"
        >
          <Link
            href={`/${normalizedLocale}/tarot-cards`}
            className="block px-4 py-2 text-sm text-[#FBFBFE] transition-colors hover:bg-[#6A3683]/25 focus:bg-[#6A3683]/25 focus:outline-none"
            role="menuitem"
            onClick={handleItemSelect}
          >
            {t('tarotCards')}
          </Link>
          <Link
            href={`/${normalizedLocale}/about-luna`}
            className="block px-4 py-2 text-sm text-[#FBFBFE] transition-colors hover:bg-[#6A3683]/25 focus:bg-[#6A3683]/25 focus:outline-none"
            role="menuitem"
            onClick={handleItemSelect}
          >
            {t('aboutLuna')}
          </Link>
        </div>
      )}
    </div>
  );
} 